import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Camera, Check, FileText, FileUp, Trash2, X } from "lucide-react";
import {
  MAX_CONSENT_PAGES,
  drawToJpegFile,
  isAcceptedConsentFile,
  isImageFile,
  isPdfFile,
  pageFileName,
  prepareConsentPage,
} from "../../utils/consentDocumentPages";
import "./ConsentDocumentCapture.css";

let pageSeq = 0;
const nextPageId = () => `page-${Date.now()}-${(pageSeq += 1)}`;

/** Phones and tablets get the native camera app (autofocus, flash, crop). */
function prefersNativeCamera() {
  if (typeof window === "undefined") return true;
  const coarse = window.matchMedia?.("(pointer: coarse)")?.matches;
  const hasStream = Boolean(navigator.mediaDevices?.getUserMedia);
  return coarse || !hasStream;
}

function DocumentCamera({ onCapture, onClose, pageCount }) {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");
  const [flash, setFlash] = useState(false);
  const [shooting, setShooting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 2560 },
            height: { ideal: 1920 },
          },
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setReady(true);
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.name === "NotAllowedError"
              ? t(
                  "consentCameraDenied",
                  "Camera access was refused. Allow the camera in the browser, or upload a file instead."
                )
              : t(
                  "consentCameraUnavailable",
                  "No camera available. Upload a scan or photo instead."
                )
          );
        }
      }
    })();
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    };
  }, [t]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const shoot = async () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth || shooting) return;
    setShooting(true);
    setFlash(true);
    window.setTimeout(() => setFlash(false), 160);
    try {
      const file = await drawToJpegFile(
        video,
        video.videoWidth,
        video.videoHeight,
        pageFileName(pageCount)
      );
      if (file) onCapture(file);
    } finally {
      setShooting(false);
    }
  };

  const full = pageCount >= MAX_CONSENT_PAGES;

  return createPortal(
    <div
      className="cdc-camera"
      role="dialog"
      aria-modal="true"
      aria-label={t("consentCameraTitle", "Photograph the consent form")}
    >
      <div className="cdc-camera__stage">
        <video
          ref={videoRef}
          className="cdc-camera__video"
          playsInline
          muted
          autoPlay
        />
        {ready ? <div className="cdc-camera__frame" aria-hidden /> : null}
        {flash ? <div className="cdc-camera__flash" aria-hidden /> : null}
        {!ready && !error ? (
          <p className="cdc-camera__status">
            {t("consentCameraStarting", "Starting camera…")}
          </p>
        ) : null}
        {error ? (
          <p className="cdc-camera__status cdc-camera__status--error" role="alert">
            {error}
          </p>
        ) : null}
      </div>

      <div className="cdc-camera__bar">
        <p className="cdc-camera__hint">
          {full
            ? t("consentCameraFull", "Page limit reached.")
            : t(
                "consentCameraHint",
                "Lay the page flat in good light, fill the frame, then capture. Repeat for each page."
              )}
        </p>
        <div className="cdc-camera__controls">
          <button
            type="button"
            className="cdc-camera__btn"
            onClick={onClose}
            aria-label={t("cancel", "Cancel")}
          >
            <X size={20} aria-hidden />
          </button>
          <button
            type="button"
            className="cdc-camera__shutter"
            onClick={shoot}
            disabled={!ready || full || shooting}
            aria-label={t("consentCameraCapture", "Capture page")}
          >
            <span />
          </button>
          <button
            type="button"
            className="cdc-camera__btn cdc-camera__btn--done"
            onClick={onClose}
          >
            <Check size={18} aria-hidden />
            <span>
              {t("consentCameraDone", "Done")}
              {pageCount ? ` (${pageCount})` : ""}
            </span>
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function PageThumb({ page, index, previewUrl, onRemove, disabled }) {
  const { t } = useTranslation();
  const [broken, setBroken] = useState(false);
  const showImage = previewUrl && isImageFile(page.file) && !broken;
  return (
    <li className="cdc-page">
      <a
        className="cdc-page__preview"
        href={previewUrl || undefined}
        target="_blank"
        rel="noreferrer"
        title={page.file.name}
      >
        {showImage ? (
          <img src={previewUrl} alt="" onError={() => setBroken(true)} />
        ) : (
          <span className="cdc-page__doc">
            <FileText size={22} aria-hidden />
            <span>{isPdfFile(page.file) ? "PDF" : page.file.name}</span>
          </span>
        )}
      </a>
      <span className="cdc-page__index">
        {t("consentPageN", "Page {{n}}", { n: index + 1 })}
      </span>
      <button
        type="button"
        className="cdc-page__remove"
        onClick={() => onRemove(page.id)}
        disabled={disabled}
        aria-label={t("consentRemovePage", "Remove page {{n}}", { n: index + 1 })}
      >
        <Trash2 size={14} aria-hidden />
      </button>
    </li>
  );
}

/**
 * Collects a signed consent form as one or more pages: camera shots of each
 * page and/or uploaded PDF / image scans. Controlled through `pages`.
 */
export default function ConsentDocumentCapture({ pages, onChange, disabled = false }) {
  const { t } = useTranslation();
  const cameraInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const pagesRef = useRef(pages);
  pagesRef.current = pages;
  const [cameraOpen, setCameraOpen] = useState(false);
  const [notice, setNotice] = useState("");

  const previews = useMemo(
    () =>
      pages.map((p) =>
        isImageFile(p.file) || isPdfFile(p.file) ? URL.createObjectURL(p.file) : null
      ),
    [pages]
  );
  useEffect(
    () => () => previews.forEach((url) => url && URL.revokeObjectURL(url)),
    [previews]
  );

  const addFiles = useCallback(
    async (fileList) => {
      const incoming = Array.from(fileList || []);
      if (!incoming.length) return;
      const accepted = incoming.filter(isAcceptedConsentFile);
      const room = MAX_CONSENT_PAGES - pagesRef.current.length;
      let msg = "";
      if (accepted.length < incoming.length) {
        msg = t("consentFileTypeRejected", "Only PDF or image files can be attached.");
      }
      if (accepted.length > room) {
        msg = t("consentPageLimit", "A form can have at most {{max}} pages.", {
          max: MAX_CONSENT_PAGES,
        });
      }
      const take = accepted.slice(0, Math.max(0, room));
      const start = pagesRef.current.length;
      const prepared = await Promise.all(
        take.map((file, i) => prepareConsentPage(file, start + i))
      );
      const next = [
        ...pagesRef.current,
        ...prepared.map((file) => ({ id: nextPageId(), file })),
      ];
      pagesRef.current = next;
      onChange(next);
      setNotice(msg);
    },
    [onChange, t]
  );

  const removePage = (id) => {
    const next = pagesRef.current.filter((p) => p.id !== id);
    pagesRef.current = next;
    onChange(next);
    setNotice("");
  };

  const openCamera = () => {
    if (prefersNativeCamera()) {
      cameraInputRef.current?.click();
    } else {
      setCameraOpen(true);
    }
  };

  const full = pages.length >= MAX_CONSENT_PAGES;

  return (
    <div className="cdc">
      <div className="cdc__actions">
        <button
          type="button"
          className="cdc__action cdc__action--primary"
          onClick={openCamera}
          disabled={disabled || full}
        >
          <Camera size={18} aria-hidden />
          <span>
            {pages.length
              ? t("consentAddPhoto", "Photograph another page")
              : t("consentTakePhoto", "Photograph the form")}
          </span>
        </button>
        <button
          type="button"
          className="cdc__action"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || full}
        >
          <FileUp size={18} aria-hidden />
          <span>{t("consentUploadFile", "Upload PDF or images")}</span>
        </button>
      </div>

      <input
        ref={cameraInputRef}
        className="cdc__hidden-input"
        type="file"
        accept="image/*"
        capture="environment"
        tabIndex={-1}
        aria-hidden
        onChange={(e) => {
          addFiles(e.target.files);
          e.target.value = "";
        }}
      />
      <input
        ref={fileInputRef}
        className="cdc__hidden-input"
        type="file"
        multiple
        accept="application/pdf,image/*,.pdf,.jpg,.jpeg,.png,.webp,.heic,.heif"
        tabIndex={-1}
        aria-hidden
        onChange={(e) => {
          addFiles(e.target.files);
          e.target.value = "";
        }}
      />

      {pages.length ? (
        <ol className="cdc__pages">
          {pages.map((page, index) => (
            <PageThumb
              key={page.id}
              page={page}
              index={index}
              previewUrl={previews[index]}
              onRemove={removePage}
              disabled={disabled}
            />
          ))}
        </ol>
      ) : (
        <p className="cdc__empty">
          {t(
            "consentPagesEmpty",
            "No pages yet. Photograph every signed page, or upload the scanned PDF."
          )}
        </p>
      )}

      {notice ? (
        <p className="cdc__notice" role="status">
          {notice}
        </p>
      ) : null}

      {cameraOpen ? (
        <DocumentCamera
          pageCount={pages.length}
          onCapture={(file) => addFiles([file])}
          onClose={() => setCameraOpen(false)}
        />
      ) : null}
    </div>
  );
}
